import { Box, Grid, GridItem, Text, IconButton, HStack, Spinner } from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

export const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

export function buildMonthCells(year, month) {
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    const offset = firstDay.getDay();
    const cells = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
        const dow = new Date(year, month - 1, d).getDay();
        cells.push({
            day: d,
            dateStr: `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
            isWeekend: dow === 0 || dow === 6,
        });
    }
    return cells;
}

/**
 * 手機專用月曆殼：緊湊固定列高(不佔滿版面)、月份導覽獨立一排(不跟其他控制項擠)。
 * 請假/訂餐/聯絡簿/補課共用同一套結構與尺寸；各頁用 getCellStyle/renderDay 客製色彩與內容。
 */
export default function MobileMonthCalendar({
    year, month, today, loading = false,
    onPrevMonth, onNextMonth, onDayClick,
    getCellStyle, renderDay, accentColor = 'blue', rowHeight = '42px',
}) {
    const cells = buildMonthCells(year, month);
    const totalRows = Math.ceil(cells.length / 7);

    return (
        <Box>
            <HStack justify="center" bg="white" border="1px solid" borderColor="gray.200"
                borderRadius="lg" boxShadow="xs" px={2} py={0.5} mb={2} w="fit-content" mx="auto">
                <IconButton icon={<FiChevronLeft />} onClick={onPrevMonth} size="xs" variant="ghost" aria-label="上個月" />
                <Text fontWeight="bold" fontSize="sm" minW="86px" textAlign="center">
                    {year}年{month}月
                    {loading && <Spinner size="xs" ml={1.5} color={`${accentColor}.400`} />}
                </Text>
                <IconButton icon={<FiChevronRight />} onClick={onNextMonth} size="xs" variant="ghost" aria-label="下個月" />
            </HStack>

            <Box border="1.5px solid" borderColor="gray.200" borderRadius="xl" boxShadow="sm" bg="white" overflow="hidden">
                <Grid templateColumns="repeat(7, 1fr)" gap={0} bg="gray.50" borderBottom="1.5px solid" borderColor="gray.200">
                    {WEEKDAYS.map((w, idx) => (
                        <GridItem key={w} borderLeft={idx === 0 ? 'none' : '1px solid'} borderColor="gray.100">
                            <Text textAlign="center" fontSize="10px" fontWeight="bold" py={1}
                                color={idx === 0 || idx === 6 ? `${accentColor}.300` : 'gray.500'}>
                                {w}
                            </Text>
                        </GridItem>
                    ))}
                </Grid>
                <Grid templateColumns="repeat(7, 1fr)" gap={0}>
                    {cells.map((cell, i) => {
                        const col = i % 7;
                        const isLastRow = Math.floor(i / 7) === totalRows - 1;
                        if (!cell) {
                            return (
                                <GridItem key={`empty-${i}`} h={rowHeight}
                                    borderRight={col < 6 ? '1px solid' : 'none'}
                                    borderBottom={!isLastRow ? '1px solid' : 'none'}
                                    borderColor="gray.100" bg="gray.50"
                                />
                            );
                        }
                        const { day, dateStr, isWeekend } = cell;
                        const isToday = dateStr === today;
                        const style = (getCellStyle && getCellStyle(dateStr, isToday)) || {};
                        const todayColor = style.todayColor || accentColor;
                        const bg = style.bg || (isToday ? `${todayColor}.50` : isWeekend ? 'gray.50' : 'white');

                        return (
                            <GridItem key={dateStr} h={rowHeight}
                                borderRight={col < 6 ? '1px solid' : 'none'}
                                borderBottom={!isLastRow ? '1px solid' : 'none'}
                                borderColor="gray.100"
                                borderTop={isToday ? '2px solid' : '0px'}
                                borderTopColor={isToday ? `${todayColor}.400` : 'transparent'}
                            >
                                <Box
                                    position="relative" h="100%" bg={bg}
                                    cursor={onDayClick ? 'pointer' : 'default'}
                                    onClick={() => onDayClick && onDayClick(dateStr)}
                                    userSelect="none"
                                >
                                    <Text pos="absolute" top="3px" left="5px" fontSize="10px"
                                        fontWeight={isToday ? 'bold' : 'normal'}
                                        color={isToday ? `${todayColor}.600` : isWeekend ? 'gray.400' : 'gray.700'}>
                                        {day}
                                    </Text>
                                    {renderDay && renderDay(dateStr, day, isToday, isWeekend)}
                                </Box>
                            </GridItem>
                        );
                    })}
                </Grid>
            </Box>
        </Box>
    );
}
